import React from 'react';
import Modal from '@mui/joy/Modal';
import ModalDialog from '@mui/joy/ModalDialog';
import ModalClose from '@mui/joy/ModalClose';
import Typography from '@mui/joy/Typography';
import Stack from '@mui/joy/Stack';
import FormControl from '@mui/joy/FormControl';
import FormLabel from '@mui/joy/FormLabel';
import FormHelperText from '@mui/joy/FormHelperText';
import Input from '@mui/joy/Input';
import Select from '@mui/joy/Select';
import Option from '@mui/joy/Option';
import Button from '@mui/joy/Button';
import Textarea from '@mui/joy/Textarea';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';

import { GENDER_OPTIONS, MARITAL_STATUS_OPTIONS, OCCUPATION_OPTIONS, EDUCATION_OPTIONS, INITIATED_WITH_OPTIONS } from '../../data/congregationOptions';
import { fetchCongregationMasters } from '../../api';
import type { CongregationMember, CongregationSpiritualMaster } from '../../types/congregation';

interface EditPersonalDialogProps {
  open: boolean;
  onClose: () => void;
  member: CongregationMember;
  onSave: (data: any) => Promise<void>;
}

export const EditPersonalDialog: React.FC<EditPersonalDialogProps> = ({ open, onClose, member, onSave }) => {
  const [loading, setLoading] = React.useState(false);
  const [masters, setMasters] = React.useState<CongregationSpiritualMaster[]>([]);

  // Fetch spiritual masters list
  React.useEffect(() => {
    const fetchMasters = async () => {
      try {
        const data = await fetchCongregationMasters();
        setMasters(data.spiritualMasters || []);
      } catch (error) {
        console.error('Failed to fetch spiritual masters:', error);
      }
    };
    
    if (open) {
      fetchMasters();
    }
  }, [open]);

  const [formData, setFormData] = React.useState({
    displayName: member.displayName || '',
    legalName: member.legalName || '',
    phone: member.phone || '',
    email: member.email || '',
    pan: member.pan || '',
    address: member.address || '',
    city: member.city || '',
    state: member.state || '',
    postalCode: member.postalCode || '',
    country: member.country || '',
    gender: member.gender || '',
    dateOfBirth: member.dateOfBirth ? dayjs(member.dateOfBirth) : null,
    maritalStatus: member.maritalStatus || '',
    occupation: member.occupation || '',
    education: member.education || '',
    spiritualMasterId: member.spiritualMasterId || null,
    initiatedWith: member.initiatedWith || 'NO',
    emergencyContactName: member.emergencyContactName || '',
    emergencyContactPhone: member.emergencyContactPhone || '',
    whatsappOptIn: member.whatsappOptIn || false,
    about: member.about || '',
  });

  const [errors, setErrors] = React.useState<Record<string, string>>({});

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const nextErrors: Record<string, string> = {};
    if (!formData.dateOfBirth) nextErrors.dateOfBirth = 'Date of birth is required.';
    if (!formData.gender) nextErrors.gender = 'Gender is required.';
    if (!formData.maritalStatus) nextErrors.maritalStatus = 'Marital status is required.';
    if (!formData.occupation) nextErrors.occupation = 'Occupation is required.';
    if (!formData.education) nextErrors.education = 'Education is required.';
    if (!formData.initiatedWith) nextErrors.initiatedWith = 'Initiated is required.';
    if (formData.initiatedWith !== 'NO' && !formData.spiritualMasterId) {
      nextErrors.spiritualMasterId = 'Spiritual master is required when initiated.';
    }
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      return;
    }
    setLoading(true);
    try {
      await onSave({
        ...formData,
        dateOfBirth: formData.dateOfBirth ? formData.dateOfBirth.format('YYYY-MM-DD') : null,
      });
      onClose();
    } catch (error) {
      console.error('Failed to save:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Modal open={open} onClose={onClose}>
        <ModalDialog sx={{ maxWidth: 600, maxHeight: '90vh', overflow: 'auto' }}>
          <ModalClose />
          <Typography level="h4" sx={{ mb: 2 }}>
            Edit Personal Information
          </Typography>
          <form onSubmit={handleSubmit}>
            <Stack spacing={2}>
              <FormControl required>
                <FormLabel>Display Name</FormLabel>
                <Input
                  value={formData.displayName}
                  onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                />
              </FormControl>

              <FormControl>
                <FormLabel>Legal Name</FormLabel>
                <Input
                  value={formData.legalName}
                  onChange={(e) => setFormData({ ...formData, legalName: e.target.value })}
                />
              </FormControl>

              <FormControl>
                <FormLabel>Phone</FormLabel>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </FormControl>

              <FormControl>
                <FormLabel>Email</FormLabel>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </FormControl>

              <FormControl required error={Boolean(errors.gender)}>
                <FormLabel>Gender</FormLabel>
                <Select
                  value={formData.gender}
                  onChange={(_, value) => setFormData({ ...formData, gender: value || '' })}
                >
                  {GENDER_OPTIONS.map((opt: { value: string; label: string }) => (
                    <Option key={opt.value} value={opt.value}>
                      {opt.label}
                    </Option>
                  ))}
                </Select>
                <FormHelperText>{errors.gender}</FormHelperText>
              </FormControl>

              <FormControl required error={Boolean(errors.dateOfBirth)}>
                <FormLabel>Date of Birth</FormLabel>
                <DatePicker
                  value={formData.dateOfBirth}
                  onChange={(newValue) => setFormData({ ...formData, dateOfBirth: newValue })}
                  slotProps={{
                    textField: {
                      size: 'small',
                      fullWidth: true,
                    },
                  }}
                />
                <FormHelperText>{errors.dateOfBirth}</FormHelperText>
              </FormControl>

              <FormControl required error={Boolean(errors.maritalStatus)}>
                <FormLabel>Marital Status</FormLabel>
                <Select
                  value={formData.maritalStatus}
                  onChange={(_, value) => setFormData({ ...formData, maritalStatus: value || '' })}
                >
                  {MARITAL_STATUS_OPTIONS.map((opt) => (
                    <Option key={opt.value} value={opt.value}>
                      {opt.label}
                    </Option>
                  ))}
                </Select>
                <FormHelperText>{errors.maritalStatus}</FormHelperText>
              </FormControl>

              <FormControl required error={Boolean(errors.occupation)}>
                <FormLabel>Occupation</FormLabel>
                <Select
                  value={formData.occupation}
                  onChange={(_, value) => setFormData({ ...formData, occupation: value || '' })}
                >
                  {OCCUPATION_OPTIONS.map((opt) => (
                    <Option key={opt.value} value={opt.value}>
                      {opt.label}
                    </Option>
                  ))}
                </Select>
                <FormHelperText>{errors.occupation}</FormHelperText>
              </FormControl>

              <FormControl required error={Boolean(errors.education)}>
                <FormLabel>Education</FormLabel>
                <Select
                  value={formData.education}
                  onChange={(_, value) => setFormData({ ...formData, education: value || '' })}
                >
                  {EDUCATION_OPTIONS.map((opt) => (
                    <Option key={opt.value} value={opt.value}>
                      {opt.label}
                    </Option>
                  ))}
                </Select>
                <FormHelperText>{errors.education}</FormHelperText>
              </FormControl>

              <FormControl required error={Boolean(errors.initiatedWith)}>
                <FormLabel>Initiated</FormLabel>
                <Select
                  value={formData.initiatedWith}
                  onChange={(_, value) => {
                    const nextValue = value || 'NO';
                    setFormData({
                      ...formData,
                      initiatedWith: nextValue,
                      spiritualMasterId: nextValue === 'NO' ? null : formData.spiritualMasterId,
                    });
                  }}
                >
                  {INITIATED_WITH_OPTIONS.map((opt) => (
                    <Option key={opt.value} value={opt.value}>
                      {opt.label}
                    </Option>
                  ))}
                </Select>
                <FormHelperText>{errors.initiatedWith}</FormHelperText>
              </FormControl>

              <FormControl error={Boolean(errors.spiritualMasterId)}>
                <FormLabel>{formData.initiatedWith && formData.initiatedWith !== 'NO' ? 'Spiritual Master *' : 'Spiritual Master'}</FormLabel>
                <Select
                  value={formData.spiritualMasterId ?? (formData.initiatedWith && formData.initiatedWith !== 'NO' ? null : 0)}
                  onChange={(_, value) => setFormData({ ...formData, spiritualMasterId: value ? Number(value) : null })}
                >
                  {(!formData.initiatedWith || formData.initiatedWith === 'NO') && (
                    <Option value={0}>— Select a spiritual master —</Option>
                  )}
                  {masters.map((master: CongregationSpiritualMaster) => (
                    <Option key={master.id} value={master.id}>
                      {master.name}
                    </Option>
                  ))}
                </Select>
                <FormHelperText>{errors.spiritualMasterId}</FormHelperText>
              </FormControl>

              <FormControl>
                <FormLabel>Address</FormLabel>
                <Textarea
                  minRows={2}
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="Enter full address (street, area, etc.)"
                />
              </FormControl>

              <Stack direction="row" spacing={2}>
                <FormControl sx={{ flex: 1 }}>
                  <FormLabel>City</FormLabel>
                  <Input
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  />
                </FormControl>

                <FormControl sx={{ flex: 1 }}>
                  <FormLabel>State</FormLabel>
                  <Input
                    value={formData.state}
                    onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                  />
                </FormControl>
              </Stack>

              <Stack direction="row" spacing={2}>
                <FormControl sx={{ flex: 1 }}>
                  <FormLabel>Postal Code</FormLabel>
                  <Input
                    value={formData.postalCode}
                    onChange={(e) => setFormData({ ...formData, postalCode: e.target.value })}
                  />
                </FormControl>

                <FormControl sx={{ flex: 1 }}>
                  <FormLabel>Country</FormLabel>
                  <Input
                    value={formData.country}
                    onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                  />
                </FormControl>
              </Stack>

              <FormControl>
                <FormLabel>PAN</FormLabel>
                <Input
                  value={formData.pan}
                  onChange={(e) => setFormData({ ...formData, pan: e.target.value })}
                />
              </FormControl>

              <FormControl>
                <FormLabel>Emergency Contact Name</FormLabel>
                <Input
                  value={formData.emergencyContactName}
                  onChange={(e) => setFormData({ ...formData, emergencyContactName: e.target.value })}
                />
              </FormControl>

              <FormControl>
                <FormLabel>Emergency Contact Phone</FormLabel>
                <Input
                  value={formData.emergencyContactPhone}
                  onChange={(e) => setFormData({ ...formData, emergencyContactPhone: e.target.value })}
                />
              </FormControl>

              <FormControl>
                <FormLabel>About</FormLabel>
                <Textarea
                  minRows={3}
                  value={formData.about}
                  onChange={(e) => setFormData({ ...formData, about: e.target.value })}
                />
              </FormControl>

              <Stack direction="row" spacing={2} justifyContent="flex-end">
                <Button variant="outlined" color="neutral" onClick={onClose} disabled={loading}>
                  Cancel
                </Button>
                <Button type="submit" loading={loading} startDecorator={<SaveRoundedIcon />}>
                  Save Changes
                </Button>
              </Stack>
            </Stack>
          </form>
        </ModalDialog>
      </Modal>
    </LocalizationProvider>
  );
};
